import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Mail, Shield, RefreshCw, Sparkles, Award, Pencil, MapPin, Save, X, Lock, Globe, Send, Gift, Copy, Tag, AlertCircle, BookOpen, ChevronDown, ChevronUp, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthContext } from '../App';
import StridesBalance from '../components/StridesBalance';
import { formatWalkedDistance, formatWalkedDuration } from '../lib/walkingStats';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = ['history', 'food', 'art', 'architecture', 'nature', 'photography', 'music', 'family'];

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const authContext = useContext(AuthContext);
  const user = authContext?.user || null;
  const token = authContext?.token || null;
  const logout = authContext?.logout || (() => {});

  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [achievements, setAchievements] = useState({ unlocked: [], locked: [] });
  const [tiers, setTiers] = useState([]);
  const [redemptions, setRedemptions] = useState({ all: [], active: [] });
  const [referral, setReferral] = useState(null);
  const [earnRules, setEarnRules] = useState(null);
  const [completions, setCompletions] = useState({ items: [], summary: null });
  const [showEarnList, setShowEarnList] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ to_username: '', amount: 100, note: '' });
  const [sending, setSending] = useState(false);
  const [redeeming, setRedeeming] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    username: '',
    display_name: '',
    bio: '',
    country: '',
    travel_interests: [],
    profile_visibility: 'public',
  });

  const loadProfile = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    // Use allSettled — a single failing sub-endpoint (e.g. referrals) must
    // NOT wipe out the whole page (was rendering blank in production).
    const results = await Promise.allSettled([
      axios.get(`${API}/profile/me`, { headers }),
      axios.get(`${API}/strides/transactions?limit=10`, { headers }),
      axios.get(`${API}/achievements/me`, { headers }),
      axios.get(`${API}/strides/discount-tiers`),
      axios.get(`${API}/strides/my-redemptions`, { headers }),
      axios.get(`${API}/referrals/me`, { headers }),
      axios.get(`${API}/strides/rules`),
      axios.get(`${API}/tour-completions/me?limit=25`, { headers }),
    ]);
    const [profileRes, txRes, achRes, tiersRes, redRes, refRes, rulesRes, compRes] = results;
    if (profileRes.status === 'fulfilled') {
      const p = profileRes.value.data;
      setProfile(p);
      setDraft({
        username: p.username || '',
        display_name: p.display_name || p.name || '',
        bio: p.bio || '',
        country: p.country || '',
        travel_interests: p.travel_interests || [],
        profile_visibility: p.profile_visibility || 'public',
      });
    } else {
      console.error('Failed to load profile', profileRes.reason);
      // Fall back to auth-context user so the page still renders.
      if (user) {
        setProfile({
          name: user.name,
          email: user.email,
          account_type: user.account_type,
          display_name: user.name,
        });
      }
    }
    if (txRes.status === 'fulfilled') setTransactions(txRes.value.data?.transactions || []);
    if (achRes.status === 'fulfilled') setAchievements(achRes.value.data || { unlocked: [], locked: [] });
    if (tiersRes.status === 'fulfilled') setTiers(tiersRes.value.data?.tiers || []);
    if (redRes.status === 'fulfilled') setRedemptions(redRes.value.data || { all: [], active: [] });
    if (refRes.status === 'fulfilled') setReferral(refRes.value.data || null);
    if (rulesRes.status === 'fulfilled') setEarnRules(rulesRes.value.data?.rules || rulesRes.value.data || null);
    if (compRes && compRes.status === 'fulfilled') {
      setCompletions({
        items: compRes.value.data?.items || [],
        summary: compRes.value.data?.summary || null,
      });
    }
  }, [token, user]);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return undefined;
    }
    loadProfile();
    const timer = setTimeout(() => { if (!user) setLoadingTimeout(true); }, 5000);
    return () => clearTimeout(timer);
  }, [token, user, navigate, loadProfile]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    localStorage.removeItem('token');
    navigate('/');
  };

  const toggleInterest = (key) => {
    setDraft((d) => ({
      ...d,
      travel_interests: d.travel_interests.includes(key)
        ? d.travel_interests.filter((i) => i !== key)
        : [...d.travel_interests, key],
    }));
  };

  const saveProfile = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/profile/me`, draft, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(data);
      setEditing(false);
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRedeem = async (tier_key) => {
    if (!token) return;
    setRedeeming(tier_key);
    try {
      await axios.post(`${API}/strides/redeem`, { tier_key }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Discount unlocked — apply at checkout');
      window.dispatchEvent(new Event('strides:updated'));
      await loadProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not redeem');
    } finally {
      setRedeeming('');
    }
  };

  const handleCancelRedemption = async (redemption_id) => {
    if (!token) return;
    try {
      await axios.post(`${API}/strides/redemptions/${redemption_id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Refunded to your Strides balance');
      window.dispatchEvent(new Event('strides:updated'));
      await loadProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel');
    }
  };

  const handleSendStrides = async () => {
    if (!token) return;
    if (!sendForm.to_username || !sendForm.amount) {
      toast.error('Username and amount required');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/strides/transfer`, sendForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent ${sendForm.amount} Strides to @${sendForm.to_username}`);
      window.dispatchEvent(new Event('strides:updated'));
      setShowSendModal(false);
      setSendForm({ to_username: '', amount: 100, note: '' });
      await loadProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSending(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referral?.code) return;
    const link = `${window.location.origin}/?ref=${referral.code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied');
    } catch {
      toast.error('Copy failed — long press to copy');
    }
  };

  const handleShareReferralLink = async () => {
    if (!referral?.code) return;
    const link = `${window.location.origin}/?ref=${referral.code}`;
    const text = `Join me on Audiostride — GPS audio walking tours. We'll both earn Strides!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Audiostride', text, url: link });
      } catch (_) { /* user cancelled */ }
    } else {
      handleCopyReferralLink();
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center pb-24">
        <div className="text-center px-4">
          {!loadingTimeout ? (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-slate-600">Loading profile...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-yellow-600" />
              </div>
              <p className="text-slate-600 mb-4">Having trouble loading your profile</p>
              <div className="space-y-3">
                <button onClick={() => window.location.reload()} className="w-full bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-semibold">Try Again</button>
                <button onClick={handleLogout} className="w-full bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full font-semibold">Log Out</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const isAdmin = profile?.account_type === 'admin';
  const isTraveler = profile?.account_type === 'traveler' || profile?.account_type === 'user';

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center text-white text-3xl font-black flex-shrink-0 shadow-lg" data-testid="profile-avatar">
                {(profile?.display_name || profile?.name || 'A').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl font-black truncate" data-testid="profile-display-name">{profile?.display_name || profile?.name || 'Traveler'}</h1>
                {profile?.username && <p className="text-teal-300 font-bold text-sm" data-testid="profile-username">@{profile.username}</p>}
                <div className="flex items-center gap-3 text-slate-300 text-sm mt-1">
                  <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {profile?.email}</span>
                  {profile?.country && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.country}</span>}
                </div>
              </div>
            </div>
            {!editing && isTraveler && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid="edit-profile-button"
                className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition active:scale-95"
                aria-label="Edit profile"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
          {isAdmin && (
            <div className="inline-flex items-center text-amber-200 bg-amber-500/20 px-3 py-1 rounded-full text-xs font-bold">
              <Shield className="w-3.5 h-3.5 mr-1" /> Admin Account
            </div>
          )}
          {profile?.bio && !editing && (
            <p className="text-slate-200 mt-4 max-w-2xl" data-testid="profile-bio">{profile.bio}</p>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Strides + actions */}
        <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-5 text-slate-900 shadow-lg" data-testid="strides-summary">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-black opacity-80 mb-1">Your Strides</p>
              <p className="text-4xl font-black">{(profile?.strides_balance ?? 0).toLocaleString()}</p>
              <p className="text-xs mt-1 opacity-80">Earn by walking, sharing, and exploring</p>
            </div>
            <Sparkles className="w-16 h-16 opacity-30" />
          </div>
          {isTraveler && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setShowSendModal(true)}
                data-testid="send-strides-button"
                className="bg-slate-900/90 hover:bg-black text-white py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition"
              >
                <Send className="w-4 h-4" />
                Send Strides
              </button>
              <button
                type="button"
                onClick={() => navigate('/leaderboard')}
                data-testid="view-leaderboard-button"
                className="bg-slate-900/90 hover:bg-black text-white py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition"
              >
                <Trophy className="w-4 h-4" />
                Leaderboard
              </button>
              <button
                type="button"
                onClick={() => navigate('/passport/me')}
                data-testid="view-passport-button"
                className="bg-gradient-to-br from-amber-600 to-orange-700 hover:opacity-90 text-white py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition"
              >
                <BookOpen className="w-4 h-4" />
                My Passport
              </button>
            </div>
          )}
        </div>

        {/* How you earn Strides — full transparency panel. Hidden behind a
            disclosure so the profile page doesn't get noisy, but the very
            first time a user lands on this page we want them to see one tap
            opens a complete list of every action + Strides + caps. Visible
            to all account types so admins can preview the traveler view. */}
        {earnRules && Object.keys(earnRules).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="earn-rules-section">
            <button
              type="button"
              onClick={() => setShowEarnList((v) => !v)}
              data-testid="earn-rules-toggle"
              className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-slate-50 transition-colors"
              aria-expanded={showEarnList}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-amber-700" />
                </div>
                <div className="min-w-0">
                  <p className="font-black text-slate-900">How you earn Strides</p>
                  <p className="text-xs text-slate-500">Every action and how many Strides it&apos;s worth — 1000 Strides = $5 off any tour</p>
                </div>
              </div>
              {showEarnList ? (
                <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
              )}
            </button>
            {showEarnList && (
              <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                <ul className="divide-y divide-slate-100">
                  {Object.entries(earnRules)
                    .filter(([, rule]) => rule && rule.enabled !== false && (rule.value ?? 0) > 0)
                    .sort(([, a], [, b]) => (b.value || 0) - (a.value || 0))
                    .map(([reason, rule]) => (
                      <li key={reason} className="flex items-center justify-between gap-3 py-3" data-testid={`earn-rule-${reason}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900">{rule.label || reason}</p>
                          {rule.daily_cap ? (
                            <p className="text-[11px] text-slate-500">Max {rule.daily_cap}× per day</p>
                          ) : null}
                        </div>
                        <span className="text-sm font-black text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 flex-shrink-0">
                          +{rule.value} Strides
                        </span>
                      </li>
                    ))}
                </ul>
                <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
                  Strides aren&apos;t cash — they&apos;re our way of saying thanks. Redeem 1000 Strides for $5 off any tour. Daily caps keep things fair for everyone.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Redeem for tour discounts */}
        {isTraveler && tiers.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200" data-testid="redeem-section">
            <h3 className="font-black text-slate-900 mb-1 flex items-center gap-2">
              <Tag className="w-5 h-5 text-teal-600" />
              Redeem for tour discounts
            </h3>
            <p className="text-xs text-slate-500 mb-4">Spend Strides for a discount you can apply at checkout</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {tiers.map((t) => {
                const balance = profile?.strides_balance ?? 0;
                const can = balance >= t.strides_cost;
                return (
                  <button
                    key={t.tier_key}
                    type="button"
                    onClick={() => can && handleRedeem(t.tier_key)}
                    disabled={!can || redeeming === t.tier_key}
                    data-testid={`redeem-tier-${t.tier_key}`}
                    className={`rounded-xl p-4 text-left border-2 transition active:scale-[0.98] ${can ? 'border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 hover:border-teal-500 hover:shadow-lg' : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'}`}
                  >
                    <p className="text-2xl font-black text-slate-900">{t.label}</p>
                    <p className="text-xs text-slate-600 mt-1">{t.description}</p>
                    <p className="text-sm font-bold text-teal-700 mt-3">{t.strides_cost.toLocaleString()} Strides</p>
                    {!can && <p className="text-[10px] text-slate-500 mt-1">Need {(t.strides_cost - balance).toLocaleString()} more</p>}
                    {redeeming === t.tier_key && <p className="text-[10px] text-teal-600 mt-1 animate-pulse">Redeeming…</p>}
                  </button>
                );
              })}
            </div>
            
            {redemptions.active.length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-emerald-500" />
                  Your active coupons
                </p>
                <div className="space-y-2">
                  {redemptions.active.map((r) => {
                    const tierMeta = tiers.find((t) => t.tier_key === r.tier_key) || {};
                    return (
                      <div key={r.redemption_id} className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid={`active-redemption-${r.redemption_id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-black text-slate-900">{tierMeta.label || `${r.discount_usd_cents / 100} off`}</p>
                          <p className="text-[11px] text-slate-600">
                            {r.status === 'pending_payment' ? 'Reserved for checkout' : 'Apply at any paid tour checkout'}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Expires {new Date(r.expires_at).toLocaleDateString()}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCancelRedemption(r.redemption_id)}
                          data-testid={`cancel-redemption-${r.redemption_id}`}
                          className="text-xs font-bold text-rose-600 hover:text-rose-700 px-2 py-1"
                        >
                          Refund
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invite friends */}
        {isTraveler && referral?.code && (
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 rounded-2xl p-5 text-white shadow-lg" data-testid="referrals-section">
            <h3 className="font-black flex items-center gap-2 mb-1">
              <Gift className="w-5 h-5 text-amber-300" />
              Invite friends · earn together
            </h3>
            <p className="text-sm text-slate-300 mb-4">
              Friends get a welcome bonus. You earn{' '}
              <span className="font-bold text-amber-300" data-testid="referral-per-signup">
                {referral.rewards?.per_signup ?? '—'}
              </span>{' '}
              Strides on signup +{' '}
              <span className="font-bold text-amber-300" data-testid="referral-per-first-complete">
                {referral.rewards?.per_first_completion ?? '—'}
              </span>{' '}
              when they finish their first tour.
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] text-slate-300 uppercase tracking-wide font-bold">Your invite link</p>
                <p className="font-bold text-white text-sm truncate" data-testid="referral-link">{`${window.location.origin}/?ref=${referral.code}`}</p>
              </div>
              <button
                type="button"
                onClick={handleCopyReferralLink}
                data-testid="copy-referral-link"
                className="bg-white/15 hover:bg-white/25 text-white p-2 rounded-lg transition active:scale-95"
                aria-label="Copy link"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            
            <button
              type="button"
              onClick={handleShareReferralLink}
              data-testid="share-referral-button"
              className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 py-2.5 rounded-xl font-black flex items-center justify-center gap-2 active:scale-95 transition shadow-lg"
            >
              <Send className="w-4 h-4" />
              Share invite
            </button>
            
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="text-center">
                <p className="text-3xl font-black" data-testid="referral-signups">{referral.signups || 0}</p>
                <p className="text-[10px] text-slate-300 uppercase tracking-wide font-bold">Friends joined</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-amber-300" data-testid="referral-completions">{referral.completions || 0}</p>
                <p className="text-[10px] text-slate-300 uppercase tracking-wide font-bold">Walked first tour</p>
              </div>
            </div>
          </div>
        )}

        {/* Edit profile form */}
        {editing && (
          <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200 space-y-4" data-testid="edit-profile-form">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-900">Edit profile</h3>
              <button type="button" onClick={() => setEditing(false)} className="text-slate-500 hover:text-slate-900" aria-label="Cancel">
                <X className="w-5 h-5" />
              </button>
            </div>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Username</span>
              <input
                type="text"
                value={draft.username}
                onChange={(e) => setDraft({ ...draft, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                placeholder="3-24 chars, letters/numbers/_"
                data-testid="edit-username-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Display name</span>
              <input
                type="text"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                data-testid="edit-display-name-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Bio</span>
              <textarea
                value={draft.bio}
                onChange={(e) => setDraft({ ...draft, bio: e.target.value.slice(0, 240) })}
                rows={3}
                data-testid="edit-bio-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
              />
              <p className="text-xs text-slate-400 text-right mt-1">{(draft.bio || '').length}/240</p>
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Country</span>
              <input
                type="text"
                value={draft.country}
                onChange={(e) => setDraft({ ...draft, country: e.target.value.slice(0, 60) })}
                data-testid="edit-country-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </label>
            <div>
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Travel interests</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {INTEREST_OPTIONS.map((opt) => {
                  const on = draft.travel_interests.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleInterest(opt)}
                      data-testid={`interest-${opt}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${on ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Profile visibility</span>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, profile_visibility: 'public' })}
                  data-testid="visibility-public"
                  className={`flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 ${draft.profile_visibility === 'public' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-700'}`}
                >
                  <Globe className="w-4 h-4" /> Public
                </button>
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, profile_visibility: 'private' })}
                  data-testid="visibility-private"
                  className={`flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 ${draft.profile_visibility === 'private' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-700'}`}
                >
                  <Lock className="w-4 h-4" /> Private
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={saveProfile}
              disabled={saving}
              data-testid="save-profile-button"
              className="w-full bg-slate-900 hover:bg-black text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition active:scale-95 disabled:opacity-60"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}

        {/* Completion history */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200" data-testid="completion-history-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-slate-900 flex items-center gap-2">
              <span className="inline-block w-5 h-5 text-lg">🎯</span>
              Completed Tours
            </h3>
            <span className="text-xs text-slate-500 font-bold" data-testid="lifetime-total">
              {completions.summary?.total_completions ?? completions.items.length} total
            </span>
          </div>
          {completions.items.length === 0 ? (
            <p className="text-slate-500 text-sm" data-testid="completion-history-empty">
              No completed tours yet. Finish a tour and it will show up here — with distance, time and badges.
            </p>
          ) : (
            <>
              {completions.summary && (
              <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-slate-100">
                <div className="text-center">
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Distance</div>
                  <div className="text-slate-900 font-black text-lg" data-testid="lifetime-distance">
                    {formatWalkedDistance(completions.summary.lifetime_distance_meters || 0)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Time</div>
                  <div className="text-slate-900 font-black text-lg font-mono" data-testid="lifetime-time">
                    {formatWalkedDuration(completions.summary.lifetime_duration_seconds || 0)}
                  </div>
                </div>
              </div>
            )}
            <ul className="space-y-2" data-testid="completion-history-list">
              {completions.items.slice(0, 8).map((c) => (
                <li
                  key={c.completion_id}
                  className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0"
                  data-testid={`completion-row-${c.completion_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-900 truncate">{c.tour_title}</div>
                    <div className="text-xs text-slate-500">
                      {c.city} · {c.stops_completed} stops
                      {c.distance_meters > 0 ? ` · ${formatWalkedDistance(c.distance_meters)}` : ''}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-slate-500">
                      {new Date(c.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </div>
                    {c.share_count > 0 && (
                      <div className="text-xs text-teal-600 font-bold">↗ {c.share_count} share{c.share_count > 1 ? 's' : ''}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            </>
          )}
        </div>

        {/* Achievements */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200" data-testid="achievements-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-slate-900 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Achievements
            </h3>
            <span className="text-xs text-slate-500 font-bold">
              {achievements.unlocked.length}/{achievements.unlocked.length + achievements.locked.length}
            </span>
          </div>
          {achievements.unlocked.length === 0 && achievements.locked.length === 0 ? (
            <p className="text-slate-500 text-sm">No achievements available yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[...achievements.unlocked, ...achievements.locked].map((a) => {
                const isLocked = !achievements.unlocked.some((u) => u.achievement_id === a.achievement_id);
                return (
                  <div
                    key={a.achievement_id}
                    data-testid={`achievement-${a.achievement_id}`}
                    className={`rounded-xl p-3 border transition ${isLocked ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'}`}
                  >
                    <div className="text-2xl mb-1">{isLocked ? '🔒' : '🏆'}</div>
                    <p className="font-black text-sm text-slate-900 leading-tight">{a.name}</p>
                    <p className="text-xs text-slate-600 mt-1 leading-snug">{a.description}</p>
                    {!isLocked && a.strides_bonus > 0 && (
                      <p className="text-[10px] text-orange-600 font-black uppercase tracking-wide mt-2">+{a.strides_bonus} Strides</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Strides transactions */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200" data-testid="transactions-section">
          <h3 className="font-black text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Recent Strides activity
          </h3>
          {transactions.length === 0 ? (
            <p className="text-slate-500 text-sm">No activity yet. Walk a tour to start earning!</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {transactions.map((tx) => (
                <div key={tx.tx_id} className="py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-slate-900 truncate">{tx.reason_label || tx.reason}</p>
                    <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`font-black text-sm ${tx.delta > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {tx.delta > 0 ? '+' : ''}{tx.delta}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Log out */}
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center justify-center bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 hover:text-rose-700 px-6 py-3 rounded-2xl font-bold transition active:scale-95"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Log Out
        </button>
      </main>
      
      {/* Send Strides modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="send-strides-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-amber-500" /> Send Strides
              </h3>
              <button type="button" onClick={() => setShowSendModal(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Send to a friend's username. They'll see it in their Strides activity.</p>
            <label className="block mb-3">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Friend's username</span>
              <div className="mt-1 flex items-center">
                <span className="text-slate-400 mr-1">@</span>
                <input
                  type="text"
                  value={sendForm.to_username}
                  onChange={(e) => setSendForm({ ...sendForm, to_username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="username"
                  data-testid="send-username-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </label>
            <label className="block mb-3">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Amount</span>
              <input
                type="number"
                min="1"
                max="10000"
                value={sendForm.amount}
                onChange={(e) => setSendForm({ ...sendForm, amount: Math.max(1, Math.min(10000, parseInt(e.target.value || '0', 10))) })}
                data-testid="send-amount-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <p className="text-[10px] text-slate-500 mt-1">You have {(profile?.strides_balance ?? 0).toLocaleString()} available</p>
            </label>
            <label className="block mb-4">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Note (optional)</span>
              <input
                type="text"
                value={sendForm.note}
                onChange={(e) => setSendForm({ ...sendForm, note: e.target.value.slice(0, 140) })}
                placeholder="Thanks for the tour rec!"
                data-testid="send-note-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </label>
            <button
              type="button"
              onClick={handleSendStrides}
              disabled={sending || !sendForm.to_username || sendForm.amount > (profile?.strides_balance ?? 0)}
              data-testid="send-strides-submit"
              className="w-full bg-slate-900 hover:bg-black disabled:bg-slate-300 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition active:scale-95"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending…' : `Send ${sendForm.amount} Strides`}
            </button>
            {sendForm.amount > (profile?.strides_balance ?? 0) && (
              <p className="text-xs text-rose-600 mt-2 text-center flex items-center justify-center gap-1">
                <AlertCircle className="w-3 h-3" /> Not enough Strides
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
